import axios, { AxiosError } from 'axios';
import i18n from 'i18next';

// Default to the same-origin `/api` path: the landing's CloudFront
// proxies `/api/*` to the API ALB (like the dashboard), so no CORS and
// no per-environment build variable are needed. VITE_API_URL can still
// override it (e.g. pointing at a remote API in local dev).
//
// The API always lives under the `/api` namespace. Guard against a bare-origin
// override (e.g. a stage VITE_API_URL=https://api.stage.done-it.app, which 404s
// on `/v4/...`): ensure the base ends in `/api`. The relative `/api` default and
// an explicit `…/api` value are both kept as-is.
const raw = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '');
const baseURL = raw === '' || raw.endsWith('/api') ? raw || '/api' : `${raw}/api`;

/**
 * Single axios instance for all Done-it API traffic.
 *
 * We don't carry auth cookies — the landing talks to the API
 * cross-origin and uses Token auth (issued by verify-email).
 * `withCredentials` stays off; CORS allow-list on the API side
 * is keyed on the landing origin (LANDING_ORIGIN env, T3).
 */
export const api = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { Accept: 'application/json' },
});

/**
 * Normalised representation of a DRF error body, suited to react-
 * hook-form's setError(field, …).
 * - field-level errors: { admin_email: 'already exists', vat: '…' }
 * - top-level non-field error: { _root: 'message' }.
 */
export interface NormalisedApiError {
  status: number;
  fields: Record<string, string>;
  message?: string;
}

export function isAxiosError(e: unknown): e is AxiosError {
  return axios.isAxiosError(e);
}

export function normaliseDrfError(error: unknown): NormalisedApiError {
  if (!isAxiosError(error)) {
    return {
      status: 0,
      fields: {},
      message: i18n.t('errors.unexpected', { ns: 'common' }),
    };
  }
  const status = error.response?.status ?? 0;
  const data = error.response?.data as Record<string, unknown> | string | undefined;
  if (typeof data === 'string') return { status, fields: {}, message: data };
  if (!data) return { status, fields: {}, message: error.message };
  const fields: Record<string, string> = {};
  let topLevel: string | undefined;
  for (const [key, value] of Object.entries(data)) {
    const flat = Array.isArray(value) ? String(value[0]) : String(value);
    if (key === 'detail' || key === 'error' || key === 'non_field_errors') {
      topLevel = flat;
    } else {
      fields[key] = flat;
    }
  }
  return { status, fields, message: topLevel };
}
